import { useState, useEffect } from "react";
import { ChevronUp, FileText, Mail, ExternalLink } from "lucide-react";

const VideoInspectTerms = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="pp-page" data-testid="videoinspect-terms-page">
      <main className="pp-main" data-testid="videoinspect-terms-content">
        <article className="pp-article">
          <header className="pp-header" data-testid="videoinspect-terms-header">
            <div className="pp-header-icon pp-header-icon--terms">
              <FileText size={28} strokeWidth={1.5} />
            </div>
            <h1 className="pp-title">Terms of Use — Video Inspect</h1>
            <p className="pp-last-updated">Last updated: April 24, 2026</p>
          </header>

          <section className="pp-section" data-testid="section-vit-intro">
            <p>
              These Terms of Use ("Terms") govern your use of the Video Inspect app ("the
              app"). By downloading or using the app, you agree to these Terms.
            </p>
          </section>

          <section className="pp-section" data-testid="section-vit-app">
            <h2 className="pp-section-heading">The App</h2>
            <p>
              Video Inspect lets you select a video and search for moments inside it using
              on-device processing. All video indexing and searching happens locally on your
              device.
            </p>
          </section>

          <section className="pp-section" data-testid="section-vit-purchases">
            <h2 className="pp-section-heading">Free Use and Purchases</h2>
            <ul className="pp-list">
              <li>You may index a limited number of videos for free.</li>
              <li>
                To index additional videos, you may make a one-time in-app purchase
                ("Unlimited") that unlocks unlimited indexing. The price is shown in the app
                before you purchase.
              </li>
              <li>
                The purchase is a one-time payment, not a subscription. There is no recurring
                charge.
              </li>
              <li>
                Purchases are processed by Apple. Apple's standard terms for in-app purchases
                apply. Refunds are handled by Apple in accordance with its policies; we do not
                process refunds directly.
              </li>
              <li>
                You can restore a previous purchase using the "Restore Purchase" option in the
                app.
              </li>
            </ul>
          </section>

          <section className="pp-section" data-testid="section-vit-acceptable">
            <h2 className="pp-section-heading">Acceptable Use</h2>
            <p>
              You agree to use the app only with video content you have the right to use, and
              only for lawful purposes. You are responsible for the videos you choose to
              process.
            </p>
          </section>

          <section className="pp-section" data-testid="section-vit-warranty">
            <h2 className="pp-section-heading">No Warranty</h2>
            <p>
              The app is provided "as is," without warranties of any kind, to the maximum
              extent permitted by law. We do not warrant that indexing or search results will be
              accurate, complete, or uninterrupted. On-device machine learning produces
              approximate results and may not find every relevant moment.
            </p>
          </section>

          <section className="pp-section" data-testid="section-vit-liability">
            <h2 className="pp-section-heading">Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, we are not liable for any indirect,
              incidental, or consequential damages arising from your use of the app. Because
              your videos are processed and stored only on your device, you are responsible for
              maintaining your own backups of your content.
            </p>
          </section>

          <section className="pp-section" data-testid="section-vit-apple">
            <h2 className="pp-section-heading">Apple's Role</h2>
            <p>
              Video Inspect is licensed, not sold, to you, under Apple's standard Licensed
              Application End User License Agreement (EULA), available at{" "}
              <a
                href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
                target="_blank"
                rel="noopener noreferrer"
                className="pp-link"
                data-testid="vit-apple-eula-link"
              >
                apple.com/legal/internet-services/itunes/dev/stdeula
                <ExternalLink size={12} className="pp-external-icon" />
              </a>
              , in addition to these Terms. If these Terms conflict with Apple's standard EULA,
              the standard EULA governs to the extent of the conflict. Apple is not responsible
              for the app or its content and has no obligation to provide support or maintenance
              for it.
            </p>
          </section>

          <section className="pp-section" data-testid="section-vit-changes">
            <h2 className="pp-section-heading">Changes to These Terms</h2>
            <p>
              We may update these Terms as the app evolves. Material changes will be reflected
              by updating the "Last updated" date above.
            </p>
          </section>

          <section className="pp-section" data-testid="section-vit-contact">
            <h2 className="pp-section-heading">Contact</h2>
            <div className="pp-contact-card" data-testid="vit-contact-card">
              <p className="pp-contact-name">Radhesh Gowd</p>
              <a
                href="mailto:support@radships.com"
                className="pp-contact-email"
                data-testid="vit-contact-email"
              >
                <Mail size={16} />
                support@radships.com
              </a>
            </div>
          </section>

          <footer className="pp-footer" data-testid="videoinspect-terms-footer">
            <p>&copy; {new Date().getFullYear()} Radhesh Gowd. All rights reserved.</p>
          </footer>
        </article>
      </main>

      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="pp-back-to-top"
          aria-label="Back to top"
          data-testid="vit-back-to-top"
        >
          <ChevronUp size={20} />
        </button>
      )}
    </div>
  );
};

export default VideoInspectTerms;
